import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Wallet, Download, Settings, ChevronDown, ChevronRight, Copy, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const peso = (n) => `\u20B1${Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Current 15-day coverage window (1st–15th or 16th–end of month)
const currentWindow = () => {
  const t = new Date();
  const y = t.getFullYear();
  const m = t.getMonth();
  if (t.getDate() <= 15) {
    return { start: toISO(new Date(y, m, 1)), end: toISO(new Date(y, m, 15)) };
  }
  return { start: toISO(new Date(y, m, 16)), end: toISO(new Date(y, m + 1, 0)) };
};

const Payroll = () => {
  const win = currentWindow();
  const [startDate, setStartDate] = useState(win.start);
  const [endDate, setEndDate] = useState(win.end);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [form, setForm] = useState(null);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

  const fetchPayroll = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      const res = await axios.get(`${API}/admin/payroll`, { ...authHeaders(), params: { start_date: startDate, end_date: endDate } });
      setData(res.data);
    } catch (error) {
      console.error('Error fetching payroll:', error);
      toast.error('Failed to load payroll');
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchPayroll(); }, [fetchPayroll]);

  const openSettings = () => {
    setForm({ ...data.settings });
    setSettingsOpen(true);
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/payroll-settings`, {
        percentage: Number(form.percentage) || 0,
        min_guaranteed: Number(form.min_guaranteed) || 0,
        min_fee_threshold: Number(form.min_fee_threshold) || 0,
        email_recipient: form.email_recipient || '',
        email_subject: form.email_subject || '',
        email_body: form.email_body || '',
      }, authHeaders());
      toast.success('Payroll settings saved');
      setSettingsOpen(false);
      fetchPayroll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
  };

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const setWindow = () => { const w = currentWindow(); setStartDate(w.start); setEndDate(w.end); };

  const exportCsv = () => {
    if (!data) return;
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [['Choreographer', 'Class', 'Date', 'Attendees', 'Total Payout', 'Fee Basis', 'Fee'].join(',')];
    data.by_choreographer.forEach((c) => {
      c.classes.forEach((g) => {
        lines.push([esc(c.instructor), esc(g.class_name), esc(g.class_date), g.attendees, Number(g.total_payout).toFixed(2), esc(g.fee_basis), Number(g.fee).toFixed(2)].join(','));
      });
      lines.push([esc(`${c.instructor} — TOTAL`), '', '', c.total_attendees, '', '', Number(c.total_fee).toFixed(2)].join(','));
      lines.push('');
    });
    lines.push(['GRAND TOTAL', '', '', '', '', '', Number(data.grand_total_fee).toFixed(2)].join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll-${startDate}_to_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Payroll CSV exported');
  };

  const copyEmail = () => {
    if (!data) return;
    const text = `To: ${data.email_recipient || '(set a recipient in Settings)'}\nSubject: ${data.email_subject_rendered}\n\n${data.email_body_rendered}`;
    navigator.clipboard.writeText(text);
    toast.success('Email copied to clipboard');
  };

  const s = data?.settings;

  return (
    <div className="p-8" data-testid="payroll-page">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-2">
        <h2 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'var(--heading-font)' }}>
          <Wallet size={28} /> Choreographer Payroll
        </h2>
        <div className="flex gap-2">
          <button onClick={openSettings} className="bg-zinc-100 hover:bg-zinc-200 rounded-full px-5 py-3 font-bold flex items-center gap-2" data-testid="payroll-settings-btn">
            <Settings size={18} /> Rules
          </button>
          <button onClick={() => setEmailOpen(true)} className="bg-black text-white hover:bg-zinc-800 rounded-full px-5 py-3 font-bold flex items-center gap-2" data-testid="payroll-email-btn">
            <Mail size={18} /> Email-Ready
          </button>
          <button onClick={exportCsv} className="bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-5 py-3 font-bold flex items-center gap-2" data-testid="payroll-export-btn">
            <Download size={18} /> Export CSV
          </button>
        </div>
      </div>
      {s && (
        <p className="text-sm text-zinc-500 mb-6" data-testid="payroll-rule-summary">
          Rules: 0 attendees → ₱0 · 1–{s.min_fee_threshold} attendees → {peso(s.min_guaranteed)} guaranteed · {s.min_fee_threshold + 1}+ attendees → {s.percentage}% of total. Attendance counts checked-in participants only.
        </p>
      )}

      {/* Coverage period */}
      <div className="flex flex-wrap items-end gap-4 mb-6 bg-white border border-zinc-200 p-4">
        <div>
          <Label htmlFor="pr-start">Coverage from</Label>
          <Input id="pr-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="payroll-start-date" />
        </div>
        <div>
          <Label htmlFor="pr-end">Coverage to</Label>
          <Input id="pr-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="payroll-end-date" />
        </div>
        <button onClick={setWindow} className="text-sm underline text-zinc-600" data-testid="payroll-current-window">Reset to current 15-day period</button>
      </div>

      {/* Grand total */}
      <div className="bg-black text-white p-5 mb-6 inline-block min-w-[260px]" data-testid="payroll-grand-total-box">
        <p className="text-xs uppercase tracking-widest text-zinc-400">Total Payout ({startDate} → {endDate})</p>
        <p className="text-4xl font-bold" style={{ fontFamily: 'var(--heading-font)', color: 'var(--accent)' }} data-testid="payroll-grand-total">{peso(data?.grand_total_fee)}</p>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-100 border-b border-zinc-200">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-bold">Choreographer</th>
              <th className="px-6 py-3 text-right text-sm font-bold">Classes</th>
              <th className="px-6 py-3 text-right text-sm font-bold">Attendees</th>
              <th className="px-6 py-3 text-right text-sm font-bold">Total Fee</th>
            </tr>
          </thead>
          <tbody>
            {(!data || data.by_choreographer.length === 0) && (
              <tr><td colSpan={4} className="px-6 py-8 text-center text-zinc-500" data-testid="payroll-empty">No checked-in classes in this period.</td></tr>
            )}
            {data?.by_choreographer.map((c) => (
              <React.Fragment key={c.instructor}>
                <tr className="border-b border-zinc-100 cursor-pointer hover:bg-zinc-50" onClick={() => toggle(c.instructor)} data-testid={`payroll-row-${c.instructor}`}>
                  <td className="px-6 py-3 font-medium">
                    <span className="inline-flex items-center gap-2">
                      {expanded.has(c.instructor) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      {c.instructor}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right">{c.class_count}</td>
                  <td className="px-6 py-3 text-right">{c.total_attendees}</td>
                  <td className="px-6 py-3 text-right font-bold" data-testid={`payroll-fee-${c.instructor}`}>{peso(c.total_fee)}</td>
                </tr>
                {expanded.has(c.instructor) && (
                  <tr className="bg-zinc-50"><td colSpan={4} className="px-6 py-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-zinc-500 border-b border-zinc-200">
                          <th className="py-2 text-left font-semibold">Class</th>
                          <th className="py-2 text-left font-semibold">Date</th>
                          <th className="py-2 text-right font-semibold">Attendees</th>
                          <th className="py-2 text-right font-semibold">Total Payout</th>
                          <th className="py-2 text-left font-semibold pl-4">Basis</th>
                          <th className="py-2 text-right font-semibold">Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.classes.map((g) => (
                          <tr key={g.class_id} className="border-b border-zinc-100">
                            <td className="py-2 font-medium">{g.class_name}</td>
                            <td className="py-2 text-zinc-600">{g.class_date}</td>
                            <td className="py-2 text-right">{g.attendees}</td>
                            <td className="py-2 text-right">{peso(g.total_payout)}</td>
                            <td className="py-2 text-zinc-500 pl-4">{g.fee_basis}</td>
                            <td className="py-2 text-right font-medium">{peso(g.fee)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Rules Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="payroll-settings-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>Payroll Rules & Email</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={saveSettings} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="pr-threshold">Min-fee up to (attendees)</Label>
                  <Input id="pr-threshold" type="number" min="0" value={form.min_fee_threshold} onChange={(e) => setForm({ ...form, min_fee_threshold: e.target.value })} data-testid="payroll-form-threshold" />
                </div>
                <div>
                  <Label htmlFor="pr-min">Min Guaranteed (₱)</Label>
                  <Input id="pr-min" type="number" min="0" value={form.min_guaranteed} onChange={(e) => setForm({ ...form, min_guaranteed: e.target.value })} data-testid="payroll-form-min" />
                </div>
              </div>
              <div>
                <Label htmlFor="pr-pct">Percentage above threshold (%)</Label>
                <Input id="pr-pct" type="number" min="0" max="100" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} data-testid="payroll-form-pct" />
                <p className="text-xs text-zinc-500 mt-1">0 attendees = ₱0 · 1–{form.min_fee_threshold} = min guaranteed · {Number(form.min_fee_threshold) + 1}+ = {form.percentage}% of total payout.</p>
              </div>
              <hr />
              <div>
                <Label htmlFor="pr-recipient">Email Recipient</Label>
                <Input id="pr-recipient" type="email" value={form.email_recipient} onChange={(e) => setForm({ ...form, email_recipient: e.target.value })} placeholder="bookkeeper@example.com" data-testid="payroll-form-recipient" />
              </div>
              <div>
                <Label htmlFor="pr-subject">Email Subject</Label>
                <Input id="pr-subject" value={form.email_subject} onChange={(e) => setForm({ ...form, email_subject: e.target.value })} data-testid="payroll-form-subject" />
              </div>
              <div>
                <Label htmlFor="pr-body">Email Body Template</Label>
                <textarea id="pr-body" rows={8} value={form.email_body} onChange={(e) => setForm({ ...form, email_body: e.target.value })} className="w-full border border-zinc-300 rounded-md p-2 text-sm font-mono" data-testid="payroll-form-body" />
                <p className="text-xs text-zinc-500 mt-1">Placeholders: {'{period}'}, {'{details}'}, {'{total}'}, {'{recipient}'}</p>
              </div>
              <button type="submit" className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold" data-testid="payroll-settings-save">SAVE</button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Email-ready Preview Dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="payroll-email-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>Email-Ready Payroll</DialogTitle>
          </DialogHeader>
          {data && (
            <div className="space-y-3">
              <div className="text-sm"><span className="text-zinc-500">To:</span> <span className="font-medium" data-testid="payroll-email-recipient">{data.email_recipient || '— set a recipient in Rules —'}</span></div>
              <div className="text-sm"><span className="text-zinc-500">Subject:</span> <span className="font-medium" data-testid="payroll-email-subject">{data.email_subject_rendered}</span></div>
              <pre className="bg-zinc-50 border border-zinc-200 rounded p-4 text-sm whitespace-pre-wrap font-sans" data-testid="payroll-email-body">{data.email_body_rendered}</pre>
              <button onClick={copyEmail} className="w-full bg-black text-white hover:bg-zinc-800 rounded-full px-6 py-3 font-bold flex items-center justify-center gap-2" data-testid="payroll-email-copy">
                <Copy size={18} /> COPY EMAIL
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payroll;
